'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ShoppingCart, Check, ShieldCheck, Zap, Wind, Eye } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';

interface ProductCardProps {
  id: string;
  name: string;
  slug: string;
  sku: string;
  sellingPrice: number;
  discountPrice?: number | null;
  brandName?: string;
  categoryName?: string;
  energyClass?: string | null;
  coolingCapacityBtu?: number | null;
  warrantyMonths?: number;
  stockQuantity?: number;
  imageUrl?: string;
}

export function ProductCard({
  id,
  name,
  slug,
  sku,
  sellingPrice,
  discountPrice,
  brandName,
  energyClass,
  coolingCapacityBtu,
  warrantyMonths = 36,
  stockQuantity = 5,
  imageUrl,
}: ProductCardProps) {
  const { addItem } = useCart();
  const { format } = useCurrency();
  const [added, setAdded] = useState(false);

  const activePrice = discountPrice && discountPrice > 0 ? discountPrice : sellingPrice;
  const hasDiscount = discountPrice && discountPrice < sellingPrice;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addItem({
      id,
      productId: id,
      name,
      sku,
      price: activePrice,
      imageUrl,
      coolingCapacityBtu,
      brandName,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div className="group relative bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/80 dark:border-slate-800/80 overflow-hidden hover:shadow-xl hover:shadow-sky-500/5 hover:border-sky-300 dark:hover:border-sky-600 transition-all duration-300 flex flex-col justify-between">
      {/* Upper Badges */}
      <div className="absolute top-3.5 left-3.5 right-3.5 flex justify-between items-center z-10 pointer-events-none">
        <div className="flex gap-1.5 flex-wrap">
          {brandName && (
            <span className="px-2.5 py-0.5 rounded-lg bg-slate-900/90 dark:bg-slate-800 text-white text-[11px] font-black tracking-wide uppercase shadow-sm backdrop-blur-sm">
              {brandName}
            </span>
          )}
          {energyClass && (
            <span className="px-2 py-0.5 rounded-lg bg-emerald-600 text-white text-[11px] font-bold tracking-wide shadow-sm flex items-center gap-1">
              <Zap className="w-3 h-3 fill-current" />
              {energyClass}
            </span>
          )}
        </div>

        {hasDiscount && (
          <span className="px-2.5 py-0.5 rounded-lg bg-rose-600 text-white text-[11px] font-black shadow-sm animate-pulse">
            Ofertë
          </span>
        )}
      </div>

      {/* Image Container with Fallback Icon */}
      <Link
        href={`/products/${slug}`}
        className="block relative bg-gradient-to-b from-slate-50 to-slate-100/70 dark:from-slate-800/50 dark:to-slate-900/80 p-8 flex items-center justify-center min-h-[200px] overflow-hidden"
      >
        <div className="w-24 h-24 rounded-2xl bg-white dark:bg-slate-800/90 shadow-md border border-slate-200/80 dark:border-slate-700/80 flex items-center justify-center text-sky-600 dark:text-sky-400 group-hover:scale-110 transition-transform duration-300">
          <Wind className="w-12 h-12" />
        </div>
        <div className="absolute inset-0 bg-sky-600/0 group-hover:bg-sky-600/5 transition-colors pointer-events-none" />
      </Link>

      {/* Body Content */}
      <div className="p-4 sm:p-5 flex flex-col flex-grow justify-between space-y-3">
        <div>
          {/* Capacity and Warranty tags */}
          <div className="flex items-center gap-2 text-[11px] font-semibold mb-1.5 flex-wrap">
            {coolingCapacityBtu && (
              <span className="bg-sky-50 dark:bg-sky-950/70 text-sky-700 dark:text-sky-300 px-2 py-0.5 rounded-md border border-sky-200/60 dark:border-sky-800/60 font-bold">
                {coolingCapacityBtu.toLocaleString()} BTU
              </span>
            )}
            <span className="flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
              <ShieldCheck className="w-3.5 h-3.5" />
              {Math.round(warrantyMonths / 12)} Vite Garanci
            </span>
          </div>

          {/* Product Title */}
          <Link href={`/products/${slug}`}>
            <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white line-clamp-2 group-hover:text-sky-600 dark:group-hover:text-sky-400 transition-colors leading-snug">
              {name}
            </h3>
          </Link>
          <div className="text-[11px] text-slate-400 dark:text-slate-500 font-mono mt-0.5">
            SKU: {sku}
          </div>
        </div>

        {/* Pricing and Action Button */}
        <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
          <div>
            <div className="text-lg sm:text-xl font-black text-slate-900 dark:text-white leading-none">
              {format(activePrice)}
            </div>
            {hasDiscount && (
              <div className="text-xs text-slate-400 dark:text-slate-500 line-through mt-0.5">
                {format(sellingPrice)}
              </div>
            )}
            <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold mt-1 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              {stockQuantity > 0 ? 'Në Gjendje (Gati për dërgim)' : 'Porosi me kërkesë'}
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <Link
              href={`/products/${slug}`}
              className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors hidden sm:flex items-center justify-center"
              title="Shiko Detajet"
            >
              <Eye className="w-4 h-4" />
            </Link>

            <button
              onClick={handleAddToCart}
              className={`p-3 rounded-2xl flex items-center justify-center transition-all duration-200 shadow-md ${
                added
                  ? 'bg-emerald-600 text-white shadow-emerald-500/25 scale-105'
                  : 'bg-slate-900 dark:bg-sky-600 text-white hover:bg-sky-600 dark:hover:bg-sky-500 shadow-slate-900/10 dark:shadow-sky-600/30 hover:scale-105'
              }`}
              title="Shto në Shportë"
            >
              {added ? <Check className="w-5 h-5" /> : <ShoppingCart className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
